import { useState, useEffect, useRef, useCallback } from 'react';
import '@aws-amplify/ui-react/styles.css';
import { Backdrop, CircularProgress } from '@mui/material';
import { Box } from '@mui/material';
import { Table } from 'components/Table';
import { defaultColumns } from './defaultColumns';
import { useTableStore } from './selectTableStore';
import { useTable } from './useTable';
import ModalTitle from '../../components/modals/ModalTitle';
import { createVideo } from '../videos/Resources';
import { useGlobalStore } from 'store/globalStore';
import { useAppProviderStore } from 'store/appProviderCopyStore';
import { CRUD_STATES } from 'constants/app';
import {
  useOutletContext,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { SubjectLayout } from 'layouts/SubjectLayout';
import { useTableFilterStore } from 'store/tableFilterStore';
import useBackgroundPolling from 'services/useBackgroundPolling';

export const VideosHome = ({
  url = '/admin/videos?limit=10&sortBy=name&offset=',
  title = '',
  listId,
  listKey = 'videoList',
  limitExceeded,
}) => {
  const { refreshData } = useGlobalStore();
  const [openTitle, setOpenTitle] = useState(false);
  const [auth, setAuth] = useOutletContext();
  const [, setUserProps] = useState({});
  const { setCancelUpload } = useAppProviderStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isUpdatingRef = useRef(false);

  const { data, page, count, setPage, isLoading, selectedItems } =
    useTableStore();

  const { videoPageFilters, setVideoPageFilter } = useTableFilterStore();

  const handleOpen = async action => {
    setOpenTitle(true);
    setCancelUpload(false);
  };

  const [initialFilterData] = useState(() => {
    if (!searchParams?.size && videoPageFilters.qs) {
      return {
        qs: videoPageFilters.qs,
        object: videoPageFilters.object,
      };
    }
    if (!searchParams?.size) return;

    let queryPath = '';
    const filterInitialArray = [];

    const channel =
      searchParams.get('canal') && JSON.parse(searchParams.get('canal'));
    const unit =
      searchParams.get('unidade') && JSON.parse(searchParams.get('unidade'));

    if (channel) {
      queryPath += `&channelId=${channel.value}`;
      filterInitialArray.push({
        field: 'channel',
        label: 'Canal',
        chipValue: channel.label,
        value: channel.value,
      });
    }
    if (unit) {
      queryPath += `&unitId=${unit.value}`;

      filterInitialArray.push({
        field: 'unit',
        label: 'Unidade',
        chipValue: unit.label,
        value: unit.value,
      });
    }

    return {
      qs: queryPath,
      object: filterInitialArray,
    };
  });

  const { getDataTable, getDataTableSilent } = useTable({
    url,
    title,
    listKey,
  });

  // Background polling integration
  const {
    isPolling,
    startBackgroundPolling,
    stopBackgroundPolling,
    updateDataHash,
    pollingState,
  } = useBackgroundPolling({
    entityType: 'videos',
    getDataTableSilent,
    getDataTable,
    listKey,
    currentFilters: videoPageFilters.qs
      ? { filter: videoPageFilters.qs }
      : initialFilterData?.qs
        ? { filter: initialFilterData.qs }
        : {},
    currentPage: page,
    currentData: data,
    isUpdatingRef,
  });

  const refreshList = useCallback(async () => {
    isUpdatingRef.current = true;

    const currentFilters = videoPageFilters.qs
      ? { filter: videoPageFilters.qs }
      : initialFilterData?.qs
        ? { filter: initialFilterData.qs }
        : {};

    try {
      const response = await getDataTable({ page, ...currentFilters });

      if (response?.[listKey]) {
        updateDataHash(response[listKey]);
      }
    } finally {
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    }
  }, [
    page,
    videoPageFilters.qs,
    initialFilterData?.qs,
    getDataTable,
    listKey,
    updateDataHash,
  ]);

  useEffect(() => {
    refreshList();
  }, [refreshData, refreshList]);

  // Refs to always hold the latest polling callbacks (avoids dep-array identity churn)
  const stopPollingRef = useRef(stopBackgroundPolling);
  stopPollingRef.current = stopBackgroundPolling;
  const startPollingRef = useRef(startBackgroundPolling);
  startPollingRef.current = startBackgroundPolling;

  // Cleanup on unmount only (empty dep array)
  useEffect(() => () => stopPollingRef.current(), []);

  // Handle navigation, user interactions and auto-start polling
  useEffect(() => {
    const isVideosPath = location.pathname.includes('/videos');
    const isEditPath = location.pathname.includes('/videos/');
    const isMainPage = isVideosPath && !isEditPath;

    // Stop polling when navigating away
    if (!isMainPage && isPolling) {
      stopPollingRef.current();
      return;
    }

    // Start polling when on main page and data hash is available
    if (isMainPage && !isPolling && !document.hidden && pollingState.lastHash) {
      const timeout = setTimeout(
        () => {
          if (!isUpdatingRef.current && !isPolling && !document.hidden) {
            startPollingRef.current();
          }
        },
        page === 0 ? 2000 : 1000
      ); // Longer delay for initial page load

      return () => clearTimeout(timeout);
    }
  }, [
    location.pathname,
    isPolling,
    page,
    pollingState.lastHash,
    videoPageFilters.qs,
  ]);
  return (
    <SubjectLayout.Root id="main-videos">
      <Backdrop open={false} style={{ zIndex: 9999 }} sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
      <SubjectLayout.Title>Vídeos</SubjectLayout.Title>
      <SubjectLayout.IconButton
        onClick={() => {
          setUserProps({});
          handleOpen({ action: CRUD_STATES.CREATE, id: '' });
        }}>
        Vídeo
      </SubjectLayout.IconButton>

      <SubjectLayout.InnerContainer>
        <div className="datatable_content">
          <div className="user_data_table_title">
            <p>Listagem de Vídeos</p>
            <div
              style={{
                display: 'flex',
                border: '1px solid #1462A5',
                padding: 12,
                borderRadius: 10,
              }}>
              <span style={{ fontSize: 12, marginRight: 5 }}>
                Farol Indicativo
                <br />
                Versões/Status:
              </span>
              <span
                className="table-videos-col-version"
                style={{
                  backgroundColor: '#98FB98',
                  marginLeft: 10,
                  marginRight: 10,
                }}></span>{' '}
              <span style={{ fontSize: 11 }}>
                Existe uma versão com status Publicado ou Agendado.
              </span>
              <span
                className="table-videos-col-version"
                style={{
                  backgroundColor: '#FFFF8F',
                  marginLeft: 10,
                  marginRight: 10,
                }}></span>{' '}
              <span style={{ fontSize: 11 }}>
                Não existe versão com status Publicado, existe uma versão com
                status Rascunho.
              </span>
              <span
                className="table-videos-col-version"
                style={{
                  backgroundColor: '#FF7F7F',
                  marginLeft: 10,
                  marginRight: 10,
                }}></span>{' '}
              <span style={{ fontSize: 11 }}>
                Não existe versão com status Publicado ou Rascunho, existe uma
                versão com status Expirado ou Erro de transcodificação.
              </span>
            </div>
          </div>
          <div className="flex-filter">
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateRows: `auto ${
                  (selectedItems?.[listId]?.length ?? 0) <= 4 ? 'auto' : '200px'
                }`,
              }}>
              <Table.Root
                data-no-dnd="true"
                sx={{
                  borderRadius: 0,
                  display: 'grid',
                }}>
                <div style={{ width: '100%' }}>
                  <Table.Filters
                    currentFilterValues={initialFilterData?.object}
                    onSubmit={(filter, filterObject) => {
                      setVideoPageFilter({ qs: filter, object: filterObject });
                      setPage(0);
                      getDataTable({ page: 0, filter });
                    }}
                  />
                </div>
                <Table.Container>
                  <Table.DataView
                    isLoading={isLoading}
                    //selected={selectedItems?.[listId] || []}
                    //onChangeSelected={(data) => onChangeSelected(listId, data)}
                    columns={defaultColumns}
                    limitExceeded={limitExceeded}
                    data={data}
                    type={'actions-videos'}
                  />
                </Table.Container>
                <Table.Pagination
                  page={page}
                  setPage={setPage}
                  isLoading={isLoading}
                  count={count}
                  onChangePage={currentPage => {
                    getDataTable({
                      page: currentPage,
                      filter: videoPageFilters.qs,
                    });
                    setPage(currentPage);
                  }}
                />
              </Table.Root>
            </Box>
          </div>
        </div>
      </SubjectLayout.InnerContainer>

      <ModalTitle
        open={openTitle}
        createVideo={createVideo}
        onClose={() => setOpenTitle(false)}
        CRUD_STATES={CRUD_STATES}
        setAuth={setAuth}
        auth={auth}
      />
    </SubjectLayout.Root>
  );
};

export default VideosHome;
